import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import { CheckCircle2, Loader2, RefreshCw, Search, XCircle } from "lucide-react";

const S = `
  .da-wrap { padding: 24px; color: #0f172a; font-family: 'Inter', sans-serif; }
  .da-head { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
  .da-title { margin:0; font-size:24px; font-weight:800; color:#0f172a; }
  .da-sub { margin-top:4px; font-size:13px; color:#64748b; }
  .da-tools { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
  .da-search { position:relative; min-width:280px; flex:1; }
  .da-search input, .da-select, .da-textarea { width:100%; box-sizing:border-box; border:1px solid #cbd5e1; border-radius:12px; padding:10px 12px; font-size:13px; color:#0f172a; background:#fff; }
  .da-search input { padding-left:34px; }
  .da-btn { border:1px solid #cbd5e1; border-radius:10px; background:#fff; color:#0f172a; padding:9px 12px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }
  .da-btn:disabled { opacity:.6; cursor:not-allowed; }
  .da-btn-success { background:#15803d; border-color:#15803d; color:#fff; }
  .da-btn-danger { background:#dc2626; border-color:#dc2626; color:#fff; }
  .da-table-wrap { background:#fff; border:1px solid #e2e8f0; border-radius:16px; overflow:auto; box-shadow:0 8px 20px rgba(15,23,42,.04); }
  .da-table { width:100%; min-width:1080px; border-collapse:collapse; }
  .da-table th { text-align:left; padding:12px; font-size:11px; color:#475569; border-bottom:1px solid #e2e8f0; text-transform:uppercase; letter-spacing:.5px; }
  .da-table td { padding:13px 12px; font-size:13px; color:#0f172a; border-bottom:1px solid #f1f5f9; vertical-align:top; }
  .da-badge { display:inline-flex; align-items:center; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:700; }
  .da-empty { padding:56px 12px; text-align:center; color:#64748b; }
`;

const badgeStyle = (status) => {
  switch (status) {
    case "PENDING": return { background: "#fef3c7", color: "#92400e" };
    case "APPROVED": return { background: "#dcfce7", color: "#166534" };
    case "REJECTED": return { background: "#fee2e2", color: "#991b1b" };
    case "DISPATCHED": return { background: "#dbeafe", color: "#1d4ed8" };
    default: return { background: "#e2e8f0", color: "#334155" };
  }
};

const fmtDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("en-IN");
};

export default function DispatchApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/sales/store-inventory/dispatch-approvals?status=${status}`);
      setRows(res.data?.data || []);
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.message || "Failed to load dispatch approvals.");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => [row.product?.design_name, row.product?.design_code, row.customer?.name, `jc-${row.job_card_id}`].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [rows, query]);

  const takeAction = async (row, action) => {
    setBusyId(row.id);
    try {
      await api.post(`/sales/store-inventory/dispatch-approvals/${row.id}/${action}`, {
        admin_notes: notes[row.id] || "",
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${action} request.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <style>{S}</style>
      <div className="da-wrap">
        <div className="da-head">
          <div>
            <h2 className="da-title">Dispatch Approvals</h2>
            <div className="da-sub">Admin approval queue and dispatch approval history with product and customer details.</div>
          </div>
          <button className="da-btn" onClick={load}><RefreshCw size={14} style={{ marginRight: 6 }} />Refresh</button>
        </div>

        <div className="da-tools">
          <div className="da-search">
            <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "#94a3b8" }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search design, customer or job card" />
          </div>
          <select className="da-select" style={{ maxWidth: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ALL">All Requests</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="DISPATCHED">Dispatched</option>
          </select>
        </div>

        {loading ? <div className="da-empty"><Loader2 size={18} className="animate-spin" /> Loading approval queue...</div> :
          error ? <div className="da-empty" style={{ color: "#b91c1c" }}>{error}</div> :
            filtered.length === 0 ? <div className="da-empty">No dispatch approvals found.</div> : (
              <div className="da-table-wrap">
                <table className="da-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Customer</th>
                      <th>Qty / Weight</th>
                      <th>Requested By</th>
                      <th>Status</th>
                      <th>Request Notes</th>
                      <th>Admin Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div style={{ fontWeight: 800 }}>{row.product?.design_name || row.product?.design_code || "-"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>JC-{row.job_card_id} | {row.product?.design_code || "-"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>Certificate: {row.product?.certificate_no || "-"}</div>
                        </td>
                        <td>
                          <div>{row.customer?.name || "-"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{row.customer?.phone || "-"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{row.customer?.email || "-"}</div>
                        </td>
                        <td>
                          <div>{row.product?.quantity || "-"} pcs</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{row.product?.final_weight_g ?? "-"} g</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>Delivery: {row.product?.delivery_status || "-"}</div>
                        </td>
                        <td>
                          <div>{row.requested_by_name || "-"}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{fmtDate(row.requested_at)}</div>
                        </td>
                        <td><span className="da-badge" style={badgeStyle(row.status)}>{row.status}</span></td>
                        <td style={{ minWidth: 200 }}>
                          <div>{row.request_notes || "-"}</div>
                          {row.status !== "PENDING" ? <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>Admin Notes: {row.admin_notes || "-"}</div> : null}
                        </td>
                        <td style={{ minWidth: 240 }}>
                          {row.status === "PENDING" ? (
                            <>
                              <textarea
                                className="da-textarea"
                                rows={3}
                                placeholder="Add approval / rejection notes"
                                value={notes[row.id] || ""}
                                onChange={(e) => setNotes((prev) => ({ ...prev, [row.id]: e.target.value }))}
                              />
                              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                                <button className="da-btn da-btn-success" disabled={busyId === row.id} onClick={() => takeAction(row, "approve")}>
                                  {busyId === row.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve
                                </button>
                                <button className="da-btn da-btn-danger" disabled={busyId === row.id} onClick={() => takeAction(row, "reject")}>
                                  {busyId === row.id ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Reject
                                </button>
                              </div>
                            </>
                          ) : (
                            <div>
                              <div>{row.approved_by_name || row.rejected_by_name || row.dispatched_by_name || "-"}</div>
                              <div style={{ fontSize: 11, color: "#64748b" }}>
                                {fmtDate(row.approved_at || row.rejected_at || row.dispatched_at)}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </>
  );
}
